import { IMAGES, buildWhatsAppUrl } from "@/lib/site";
import { BedDouble, Users, ArrowUpRight, Check } from "lucide-react";

const rooms = [
  {
    id: "couple-family",
    name: "Couple & Family Room",
    tag: "Intimate • Cozy • Private",
    image: IMAGES.roomCouple,
    icon: BedDouble,
    description:
      "A warm, private room with a comfortable king-size bed, wooden interiors, and a beautiful window view opening into the coffee estate. Perfect for couples on a romantic getaway or small families looking for cozy comfort.",
    features: [
      "King-size bed with premium linens",
      "Private attached bathroom",
      "Garden & estate window view",
      "Traditional Malnad interiors",
      "Ideal for 2–4 guests",
    ],
  },
  {
    id: "group",
    name: "The Malnad Group Dorm",
    tag: "Spacious • Vaulted Ceiling • Best for Friends",
    image: IMAGES.roomGroup,
    icon: Users,
    description:
      "A large, airy hall with a striking wooden vaulted roof and multiple beds — ideal for friends, colleagues on a company trip, or a big family reunion. Plenty of room to hang out, share stories, and laugh late into the night.",
    features: [
      "Multiple double & single beds",
      "Common seating area inside the room",
      "Perfect for 8–12 guests",
      "Great for friends, groups & offsites",
    ],
  },
];

const Rooms = () => {
  return (
    <section
      id="rooms"
      data-testid="rooms-section"
      className="relative py-24 md:py-32 bg-[#F8FAF7]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              Our Rooms
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1B4332] leading-[1.05] tracking-tight">
            Two ways to{" "}
            <span className="italic font-light">stay with us</span>
          </h2>
          <p className="mt-5 text-[#1A1A1A]/75 text-base md:text-lg leading-relaxed">
            Whether you're planning an intimate getaway or a big group escape,
            we have a room for you — thoughtfully designed with the warmth of
            traditional Malnad architecture.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          {rooms.map((r, i) => (
            <article
              key={r.id}
              className="reveal group rounded-3xl overflow-hidden bg-white border border-[#1B4332]/10 shadow-sm hover:-translate-y-1 hover:shadow-2xl transition-all duration-500"
              style={{ animationDelay: `${i * 0.12}s` }}
              data-testid={`room-card-${r.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#1B4332]">
                <img
                  src={r.image}
                  alt={r.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1B4332] text-[10px] tracking-[0.25em] uppercase font-medium">
                  <r.icon size={13} strokeWidth={1.8} />
                  {r.tag}
                </div>
              </div>

              <div className="p-7 md:p-9">
                <h3 className="font-heading text-2xl md:text-3xl text-[#1B4332] leading-tight">
                  {r.name}
                </h3>
                <p className="mt-3 text-[#1A1A1A]/70 leading-relaxed text-sm md:text-base">
                  {r.description}
                </p>

                <ul className="mt-5 space-y-2">
                  {r.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-[#1A1A1A]/80"
                    >
                      <Check
                        size={16}
                        strokeWidth={2.2}
                        className="text-[#D4AF37] mt-0.5 shrink-0"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={buildWhatsAppUrl(
                    `Hi Navilukallu Homestay, I'd like to check availability of the *${r.name}*. Please share tariff & dates.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`room-enquire-${r.id}`}
                  className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1B4332] text-white text-sm hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors"
                >
                  Enquire on WhatsApp
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Rooms;
